"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, Target, Zap, TrendingUp, DollarSign } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Discovery",
  description: "Analyzing your digital footprint to identify expansion opportunities and technical gaps in your current presence.",
  icon: Search
}, {
  title: "Strategy",
  description: "Developing a custom blueprint for sustainable growth, brand authority, and high-performance user experiences.",
  icon: Target
}, {
  title: "Implementation",
  description: "Building your high-performance WordPress ecosystem and launching targeted SEO and social media campaigns.",
  icon: Zap
}, {
  title: "Growth",
  description: "Scaling your presence through content mastery, continuous optimization, and long-term relationship building.",
  icon: TrendingUp
}];
function TimelineStep({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="143487" data-index={index} ref={ref} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-24 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`hidden md:flex w-1/2 ${isEven ? "justify-end pr-16 text-right" : "justify-start pl-16 order-last"}`}>
        <div className="max-w-md">
          <div className="text-[80px] font-mono font-bold leading-none tracking-tighter mb-4" style={{
          WebkitTextStroke: "2px #06b6d4",
          color: "#06b6d4"
        }}>
            {(index + 1).toString().padStart(2, "0")}
          </div>
          <h3 className="text-3xl font-serif font-bold mb-3 text-foreground tracking-tight">
            {step.title}
          </h3>
          <p className="text-muted-foreground text-lg font-sans leading-relaxed">
            {step.description}
          </p>
        </div>
      </div>

      {}
      <div className="absolute left-0 md:left-1/2 top-8 md:top-1/2 -translate-y-1/2 md:-translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-background border-2 border-primary z-10 shadow-[0_0_20px_rgba(20,184,166,0.4)]">
        <step.icon className="w-5 h-5 text-primary" />
      </div>

      {}
      <div className="flex md:hidden w-full pl-20 pt-2">
        <div>
          <div className="text-[64px] font-mono font-bold leading-none tracking-tighter mb-2" style={{
          WebkitTextStroke: "1.5px #06b6d4",
          color: "#06b6d4"
        }}>
            {(index + 1).toString().padStart(2, "0")}
          </div>
          <h3 className="text-2xl font-serif font-bold mb-2 text-foreground tracking-tight">
            {step.title}
          </h3>
          <p className="text-muted-foreground text-base font-sans leading-relaxed">
            {step.description}
          </p>
        </div>
      </div>
    </div>;
}
export default function TheRoadmapToScaling() {
  const [lineHeight, setLineHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startOffset = windowHeight / 2;
      const scrollProgress = (startOffset - rect.top) / (rect.height - 100);
      const clampedProgress = Math.max(0, Math.min(1, scrollProgress));
      setLineHeight(clampedProgress * 100);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="the-roadmap-to-success" className="dark bg-background text-foreground py-32 px-6 md:px-12 overflow-hidden relative">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="text-sm font-mono font-bold tracking-widest uppercase text-primary mb-4">
            Our Methodology
          </h2>
          <h3 className="text-4xl md:text-6xl font-serif font-bold tracking-tight mb-6">
            The Roadmap to Success
          </h3>
          <p className="text-xl font-sans text-muted-foreground">
            A precise, tech-driven framework designed to elevate your brand and build lasting digital partnerships.
          </p>
        </div>

        {}
        <div ref={containerRef} className="relative max-w-5xl mx-auto pb-24">
          {}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-border/20 md:-translate-x-1/2" />
          
          {}
          <div className="absolute left-6 md:left-1/2 top-0 w-px bg-gradient-to-b from-primary to-cyan-500 md:-translate-x-1/2 shadow-[0_0_15px_rgba(20,184,166,0.6)]" style={{
          height: `${lineHeight}%`
        }} />

          {}
          <div className="relative z-10 pt-12">
            {steps.map((step, i) => <TimelineStep key={i} step={step} index={i} />)}
          </div>

          {}
          <div className="relative flex justify-start md:justify-center mt-12 pl-0 md:pl-0">
            <div className="absolute left-6 md:left-1/2 -translate-x-1/2 flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-background border-2 border-primary flex items-center justify-center shadow-[0_0_40px_rgba(20,184,166,0.5)] animate-pulse">
                <DollarSign className="w-10 h-10 text-primary" />
              </div>
              <span className="mt-6 text-xl font-mono font-bold tracking-widest uppercase text-primary">
                Success
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}